import { useEffect, useRef } from "react";
import cn from "classnames";
import { Text } from "../../../../../packages/ui-kit";
import { useInputHeightContext } from "../../../contexts";
import { DICTIONARY } from "../../../dictionary";
import { File } from "./file";
import type { AttachedFiles } from "../../../types";
import styles from "./file-list.module.scss";

type Props = {
  hasFiles: boolean;
  files: AttachedFiles;
  hasInvalidFiles: boolean;
  isFileAttached: boolean;
  isFileLimitExceeded: boolean;
  onRemoveClick: (id: string) => () => void;
};

export const FileList = ({
  files,
  hasFiles,
  hasInvalidFiles,
  isFileAttached,
  onRemoveClick,
  isFileLimitExceeded,
}: Props) => {
  const { fileListHeight } = useInputHeightContext();
  const bottomRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    bottomRef?.current?.scrollIntoView?.({ behavior: "smooth" });
  };

  useEffect(() => {
    // делает прокрутку после добавления файла
    // timeout необходим, так как обработка файлового сообщения занимает время
    const isAttachedFileInvalid = isFileAttached && hasInvalidFiles;

    if (isFileAttached || isFileLimitExceeded || isAttachedFileInvalid) {
      setTimeout(handleScroll, 100);
    }
  }, [isFileAttached, isFileLimitExceeded, hasInvalidFiles]);

  if (!hasFiles) {
    return null;
  }

  const errorStyles = cn(styles["max-limit-error"], styles["file-error"]);

  const filesList = files.map(({ id, error, file }) => (
    <File
      key={id}
      error={error}
      file={file}
      onRemoveClick={onRemoveClick(id)}
    />
  ));

  const maxLimitErrorText = (
    <Text
      dataTestId="max-file-limit-error"
      size="xsmall"
      className={errorStyles}
    >
      {DICTIONARY.FILE.MAX_LIMIT}
    </Text>
  );

  const invalidFilesErrorText = (
    <Text
      dataTestId="max-file-limit-error"
      size="xsmall"
      className={errorStyles}
    >
      {DICTIONARY.FILE.INVALID_FILES}
    </Text>
  );

  return (
    <div
      data-test-id="file-list"
      className={styles["file-list"]}
      style={{ height: `${fileListHeight}px` }}
    >
      {filesList}
      {!isFileLimitExceeded && hasInvalidFiles && invalidFilesErrorText}
      {isFileLimitExceeded && maxLimitErrorText}
      <div data-test-id="file-list-bottom" ref={bottomRef} />
    </div>
  );
};
